/**
 * Canonical ordering for /search query params. One source of truth so client
 * navigation and the edge redirect emit the same URL for a given filter set,
 * collapsing the many URL variants a crawler would otherwise see.
 */
export const SEARCH_PARAM_ORDER = [
  'q',
  'order',
  'tags',
  'makes',
  'gears',
  'calibrated',
  'sizes',
  'creators',
  // Sits with `creators` because it refines the same scope. Opt-in here
  // (`verified=true` = verified only); /top-creators uses the same key with the
  // opposite default, where only `verified=false` is meaningful.
  'verified',
  'format',
  'architecture',
  'page',
] as const;

const ORDER_INDEX = new Map<string, number>(
  SEARCH_PARAM_ORDER.map((key, i) => [key, i])
);

/**
 * Serialize params with known keys in SEARCH_PARAM_ORDER, then any unknown keys
 * in their original relative order (so rpc/tab/authorization_id etc. survive).
 * Stable and idempotent: feeding the output back in yields the same string.
 */
export function orderSearchParams(params: URLSearchParams): string {
  const entries = Array.from(params.entries());
  // Array.prototype.sort is stable, so ties (unknown keys, or repeats of the
  // same key) keep their original relative order.
  entries.sort(([a], [b]) => {
    const ai = ORDER_INDEX.get(a) ?? Infinity;
    const bi = ORDER_INDEX.get(b) ?? Infinity;
    return ai - bi;
  });
  const ordered = new URLSearchParams();
  for (const [key, value] of entries) {
    ordered.append(key, value);
  }
  return ordered.toString();
}
